import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';

interface DebtorInfoTabProps {
  debtor: any;
}

export default function DebtorInfoTab({ debtor }: DebtorInfoTabProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Data Pribadi */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Data Pribadi</h3>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Jenis Identitas</p>
              <p className="font-medium">{debtor.id_type?.toUpperCase() || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">No. Identitas</p>
              <p className="font-medium">{debtor.id_number || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Tempat, Tanggal Lahir</p>
              <p className="font-medium">
                {debtor.birth_place || '-'}, {formatDate(debtor.birth_date)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Jenis Kelamin</p>
              <p className="font-medium">
                {debtor.gender === 'male'
                  ? 'Laki-laki'
                  : debtor.gender === 'female'
                    ? 'Perempuan'
                    : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status Pernikahan</p>
              <p className="font-medium capitalize">{debtor.marital_status || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Tipe Debitur</p>
              <p className="font-medium">
                {debtor.debtor_type === 'individual' ? 'Perorangan' : 'Badan Usaha'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Kontak */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Kontak</h3>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-gray-400" />
            <span>{debtor.phone || '-'}</span>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-gray-400" />
            <span>{debtor.email || '-'}</span>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p>{debtor.address || '-'}</p>
              <p className="text-sm text-gray-500">
                {[debtor.city, debtor.province, debtor.postal_code].filter(Boolean).join(', ') ||
                  '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Pekerjaan */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Data Pekerjaan</h3>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Pekerjaan</p>
              <p className="font-medium">{debtor.occupation || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Perusahaan</p>
              <p className="font-medium">{debtor.company_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Penghasilan Bulanan</p>
              <p className="font-medium">{formatCurrency(debtor.monthly_income)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Sumber Penghasilan</p>
              <p className="font-medium">{debtor.source_of_income || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Keluarga */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Data Keluarga</h3>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Nama Pasangan</p>
              <p className="font-medium">{debtor.spouse_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">No. Identitas Pasangan</p>
              <p className="font-medium">{debtor.spouse_id_number || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Kontak Darurat</p>
              <p className="font-medium">{debtor.emergency_contact_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">No. Telepon Darurat</p>
              <p className="font-medium">{debtor.emergency_contact_phone || '-'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
